import * as React from 'react';
import { useState } from 'react';
import { Trophy, Users, Clock, Gift, ChevronRight, Plus, Key } from 'lucide-react';
import type { Tournament } from '../../../types/features';
import { tournamentService } from '../../../features/tournaments/tournamentService';
import { TournamentDetailsModal } from './TournamentDetailsModal';
import { CreateTournamentModal } from './CreateTournamentModal';

export const TournamentsPanel: React.FC = () => {
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
    const [activeTab, setActiveTab] = useState<'active' | 'upcoming'>('active');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isJoinByCodeModalOpen, setIsJoinByCodeModalOpen] = useState(false);
    const [inviteCode, setInviteCode] = useState('');
    const [joinError, setJoinError] = useState('');

    const tournaments = activeTab === 'active'
        ? tournamentService.getActiveTournaments()
        : tournamentService.getUpcomingTournaments();

    const handleTournamentCreated = (tournament: Tournament) => {
        // Refresh the tournaments list
        if (tournament.status === 'active') {
            setActiveTab('active');
        } else if (tournament.status === 'upcoming') {
            setActiveTab('upcoming');
        }
    };

    const formatTimeLeft = (endDate: string) => {
        const end = new Date(endDate);
        const now = new Date();
        const diff = end.getTime() - now.getTime();

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (days > 0) return `${days}d ${hours}h left`;
        return `${hours}h left`;
    };

    const formatPrizePool = (tournament: Tournament) => {
        if (tournament.isPrizePoolProgressive) {
            return `Up to ${tournament.prizePool} coins`;
        }
        return `${tournament.prizePool} coins`;
    };

    const handleJoinByCode = () => {
        const tournament = tournamentService.getTournaments().find(t =>
            t.type === 'private' && t.inviteCode === inviteCode &&
            (t.status === 'active' || t.status === 'upcoming')
        );

        if (!tournament) {
            setJoinError('Invalid or expired invite code');
            return;
        }

        const joined = tournamentService.joinTournament(tournament.id, player.id);
        if (joined) {
            setIsJoinByCodeModalOpen(false);
            setSelectedTournament(tournament);
        } else {
            setJoinError('Failed to join tournament. You might already be a participant.');
        }
    };

    return (
        <div className="w-full">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-yellow-500" />
                    Tournaments
                </h2>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setIsJoinByCodeModalOpen(true)}
                        className="px-4 py-2 border border-yellow-500 text-yellow-600 hover:bg-yellow-50 
                            rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Key className="w-4 h-4" />
                        Join with Code
                    </button>
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg 
                            transition-colors flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Create Tournament
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-4 mb-4">
                <button
                    className={`px-4 py-2 rounded-lg transition-colors ${activeTab === 'active'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    onClick={() => setActiveTab('active')}
                >
                    Active
                </button>
                <button
                    className={`px-4 py-2 rounded-lg transition-colors ${activeTab === 'upcoming'
                        ? 'bg-yellow-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    onClick={() => setActiveTab('upcoming')}
                >
                    Upcoming
                </button>
            </div>

            {/* Tournament List */}
            <div className="space-y-4">
                {tournaments.length === 0 ? (
                    <div className="text-center py-8">
                        <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-500">
                            No {activeTab} tournaments available
                        </p>
                        <button
                            onClick={() => setIsCreateModalOpen(true)}
                            className="mt-4 px-4 py-2 text-yellow-600 hover:text-yellow-700 
                                border border-yellow-600 hover:border-yellow-700 rounded-lg 
                                transition-colors flex items-center gap-2 mx-auto"
                        >
                            <Plus className="w-4 h-4" />
                            Create One
                        </button>
                    </div>
                ) : (
                    tournaments.map((tournament) => (
                        <div
                            key={tournament.id}
                            className="bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => setSelectedTournament(tournament)}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-semibold">
                                    {tournament.name}
                                </h3>
                                <span className="text-sm text-gray-500">
                                    {formatTimeLeft(tournament.endDate)}
                                </span>
                            </div>

                            <div className="relative h-32 mb-3 rounded-lg overflow-hidden">
                                <img
                                    src={tournament.imageUrl}
                                    alt={tournament.name}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
                                    {tournament.type === 'private' ? 'Private' : 'Public'}
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center text-gray-600">
                                        <Users className="w-4 h-4 mr-1" />
                                        <span>
                                            {tournament.participants.length}
                                            {tournament.maxParticipants
                                                ? `/${tournament.maxParticipants}`
                                                : ''}
                                        </span>
                                    </div>
                                    <div className="flex items-center text-gray-600">
                                        <Gift className="w-4 h-4 mr-1" />
                                        <span>{formatPrizePool(tournament)}</span>
                                    </div>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Join by Code Modal */}
            {isJoinByCodeModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <Key className="w-5 h-5 text-yellow-500" />
                            Join Tournament
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Invite Code
                                </label>
                                <input
                                    type="text"
                                    value={inviteCode}
                                    onChange={(e) => {
                                        setInviteCode(e.target.value);
                                        setJoinError('');
                                    }}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500"
                                    placeholder="Enter invite code"
                                />
                            </div>
                            {joinError && (
                                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                                    {joinError}
                                </div>
                            )}
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setIsJoinByCodeModalOpen(false)}
                                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleJoinByCode}
                                    disabled={!inviteCode.trim()}
                                    className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white 
                                        rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Join
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            {selectedTournament && (
                <TournamentDetailsModal
                    tournament={selectedTournament}
                    onClose={() => setSelectedTournament(null)}
                />
            )}
            {isCreateModalOpen && (
                <CreateTournamentModal
                    onClose={() => setIsCreateModalOpen(false)}
                    onSuccess={handleTournamentCreated}
                />
            )}
        </div>
    );
}; 